import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/Card';
import { Button } from '../ui/Button';
import { Progress } from '../ui/Progress';
import { Alert, AlertDescription } from '../ui/Alert';
import { dashboardService } from '../../services/dashboard.service';
import { handleApiCall, displayError } from '../../lib/error-handler';
import { Play, X, Loader2 } from 'lucide-react';
import type { Job, JobProgress } from '../../types/api';

interface JobRunnerProps {
  onJobStart: (job: Job) => void;
  onJobComplete: (jobId: string) => void;
  onJobError: () => void;
  currentJob: Job | null;
  disabled?: boolean;
}

export function JobRunner({ onJobStart, onJobComplete, onJobError, currentJob, disabled }: JobRunnerProps) {
  const [progress, setProgress] = useState<JobProgress | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);

  const stopPollingRef = useRef<(() => void) | null>(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (stopPollingRef.current) {
        stopPollingRef.current();
      }
    };
  }, []);

  // Track current job progress
  useEffect(() => {
    if (currentJob) {
      startProgressTracking(currentJob.id);
    } else {
      stopProgressTracking();
    }

    return () => {
      stopProgressTracking();
    };
  }, [currentJob?.id]);

  const startProgressTracking = (jobId: string) => {
    setError(null);

    // Stop any existing polling
    if (stopPollingRef.current) {
      stopPollingRef.current();
    }

    // Start polling
    stopPollingRef.current = dashboardService.trackJobProgress(
      jobId,
      handleProgressUpdate,
      handleProgressError
    );
  };

  const handleProgressError = (error: Error) => {
    console.error('Progress tracking error:', error);
    setError('Connection lost. Retrying...');
  };

  const handleProgressUpdate = (progressData: JobProgress) => {
    setProgress(progressData);

    if (progressData.status === 'completed') {
      stopProgressTracking();
      onJobComplete(progressData.jobId);
    } else if (progressData.status === 'failed' || progressData.status === 'cancelled') {
      stopProgressTracking();
      setError(`Job ${progressData.status}. Please try again.`);
      onJobError();
    }
  };

  const stopProgressTracking = () => {
    if (stopPollingRef.current) {
      stopPollingRef.current();
      stopPollingRef.current = null;
    }
  };

  const handleStartJob = async () => {
    setStarting(true);
    setError(null);

    try {
      const job = await handleApiCall(
        () => dashboardService.createJob({
          min_relevance_score: 50
        }),
        'Failed to start analysis'
      );

      onJobStart(job);
    } catch (err) {
      setError(displayError(err));
    } finally {
      setStarting(false);
    }
  };

  const handleCancelJob = async () => {
    if (!currentJob) return;

    if (!window.confirm('Are you sure you want to cancel this job?')) {
      return;
    }

    try {
      await handleApiCall(
        () => dashboardService.cancelJob(currentJob.id),
        'Failed to cancel job'
      );

      stopProgressTracking();
      onJobError();
    } catch (err) {
      setError(displayError(err));
    }
  };

  const isRunning = currentJob && progress && ['pending', 'running'].includes(progress.status);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Run Analysis</CardTitle>
        <CardDescription>
          Start analyzing RSS feeds with your criteria
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {isRunning && progress ? (
          <div className="space-y-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin text-primary" />
                  <span className="font-medium">{progress.currentStep}</span>
                </div>
                <span className="text-muted-foreground">{progress.progress}%</span>
              </div>
              <Progress value={progress.progress} />
            </div>

            {progress.totalArticles > 0 && (
              <div className="text-sm text-muted-foreground">
                Processing article {progress.processedArticles} of {progress.totalArticles}
              </div>
            )}

            <Button
              variant="destructive"
              onClick={handleCancelJob}
              className="w-full"
              size="sm"
            >
              <X className="w-4 h-4 mr-2" />
              Cancel Job
            </Button>
          </div>
        ) : (
          <Button
            onClick={handleStartJob}
            disabled={disabled || starting}
            className="w-full"
          >
            {starting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Starting...
              </>
            ) : (
              <>
                <Play className="w-4 h-4 mr-2" />
                Run Analysis
              </>
            )}
          </Button>
        )}

        {disabled && !isRunning && (
          <p className="text-sm text-muted-foreground text-center">
            Add and enable at least one RSS feed to run analysis
          </p>
        )}
      </CardContent>
    </Card>
  );
}
